import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Package, Boxes, Loader2, Tag } from 'lucide-react';
import { Card, CardContent, Button, Badge, Dialog, cn } from '../../ui';
import { getIcon } from '../icons';
import { useTiposCrud } from '../hooks/useTiposCrud';
import ScopeTabs from '../components/ScopeTabs';
import TipoForm from '../components/TipoForm';
import GuiaRapida from '../components/GuiaRapida';
import CategoriasManager from '../components/CategoriasManager';

/**
 * Página: Gestión de tipos de artículo (sub-bloque III.4)
 *
 * Sustituye el placeholder de III.1. Dos modos en la misma pantalla
 * (igual que el sneak peek del mockup v3, sin tocar el routing):
 *
 *   modo 'lista'  → tarjetas de tipos del scope activo + acciones
 *                   (Nuevo tipo / Editar / Eliminar)
 *   modo 'crear'  → TipoForm en blanco
 *   modo 'editar' → TipoForm precargado
 *
 * Datos vía useTiposCrud (mock mientras no haya backend II.4.5; ver
 * hooks/useTiposCrud.js para activar el modo real el lunes).
 *
 * NOTA: ScopeTabs se reutiliza del bloque III.2 (components/ScopeTabs).
 */

export default function TiposList() {
  const [scope, setScope] = useState('interno');
  const [view, setView] = useState({ mode: 'lista', tipo: null });
  const [deleting, setDeleting] = useState(null); // tipo a confirmar borrado
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [formErr, setFormErr] = useState(null);
  const [formDup, setFormDup] = useState(null); // duplicados al pasar a «bloquear» (01/11)
  const [openCats, setOpenCats] = useState(false);
  // Bumpea cuando se editan categorías para refrescar el select del form.
  const [reloadKey, setReloadKey] = useState(0);

  const {
    tipos,
    loading,
    error,
    createTipo,
    updateTipo,
    deleteTipo,
    suggestPrefix,
    isMock,
  } = useTiposCrud(scope);

  function goList() {
    setFormErr(null);
    setFormDup(null);
    setView({ mode: 'lista', tipo: null });
  }

  async function handleCreate(payload) {
    setFormErr(null);
    try {
      await createTipo(payload);
      goList();
    } catch (err) {
      setFormErr(err?.response?.data?.error || err?.message || 'Error al crear el tipo');
    }
  }
  async function handleUpdate(payload) {
    setFormErr(null);
    setFormDup(null);
    try {
      await updateTipo(view.tipo.id, payload);
      goList();
    } catch (err) {
      setFormErr(err?.response?.data?.error || err?.message || 'Error al guardar los cambios');
      // Pasar a «bloquear» con duplicados (01/11): el servidor dice quiénes y qué activos.
      const d = err?.response?.data?.details;
      setFormDup(d && d.code === 'una_por_persona_duplicados' ? d.duplicados || [] : null);
    }
  }
  async function confirmDelete() {
    setDeleteBusy(true);
    try {
      await deleteTipo(deleting.id);
      setDeleting(null);
    } finally {
      setDeleteBusy(false);
    }
  }

  // ── Modo form ──
  if (view.mode === 'crear' || view.mode === 'editar') {
    return (
      <div className="px-6 py-5">
        <div className="mb-5">
          <h1 className="text-xl font-semibold text-slate-900 leading-tight">
            {view.mode === 'crear' ? 'Nuevo tipo de artículo' : 'Editar tipo'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            <button onClick={goList} className="text-[hsl(var(--brand-light))] hover:underline">
              ← Volver al catálogo
            </button>
          </p>
        </div>
        {formErr && (
          <div className="mb-4 bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-700">
            <div className="font-medium mb-0.5">No se pudo guardar:</div>
            <div className="text-xs">{formErr}</div>
            {/^Cambiar el tipo de un campo existente/.test(formErr) && (
              <div className="text-xs text-rose-600 mt-2 pt-2 border-t border-rose-200">
                💡 Para cambiar el tipo de un campo ya creado, elimínalo y créalo
                de nuevo con el tipo deseado. Los datos previos de esa columna
                se perderán.
              </div>
            )}
            {formDup && formDup.length > 0 && (
              <ul className="text-xs text-rose-700 mt-2 pt-2 border-t border-rose-200 space-y-0.5">
                {formDup.map((d) => (
                  <li key={d.persona_id}>
                    <span className="font-medium">{d.nombre}</span>: {d.activos.map((a) => a.referencia).join(', ')}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <TipoForm
          mode={view.mode}
          initial={view.tipo}
          onCancel={goList}
          onSubmit={view.mode === 'crear' ? handleCreate : handleUpdate}
          suggestPrefix={suggestPrefix}
          reloadKey={reloadKey}
        />
        <CategoriasManager
          open={openCats}
          onClose={() => setOpenCats(false)}
          onChange={() => setReloadKey((k) => k + 1)}
        />
      </div>
    );
  }

  // ── Modo lista ──
  return (
    <div className="px-6 py-5">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 leading-tight">
            Tipos de artículo
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Catálogo de tipos definidos · cada tipo agrupa unidades concretas
            {isMock && (
              <span className="ml-2 text-[11px] text-amber-600 font-medium">
                · datos de demostración (backend pendiente)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <GuiaRapida seccion="tipos" />
          <Button variant="secondary" onClick={() => setOpenCats(true)}>
            <Tag className="w-4 h-4 mr-1" />
            Gestionar categorías
          </Button>
          <Button onClick={() => setView({ mode: 'crear', tipo: { scope } })}>
            <Plus className="w-4 h-4 mr-1" />
            Nuevo tipo
          </Button>
        </div>
      </div>

      <CategoriasManager
        open={openCats}
        onClose={() => setOpenCats(false)}
        onChange={() => setReloadKey((k) => k + 1)}
      />

      <ScopeTabs value={scope} onChange={setScope} />

      {loading && (
        <Card>
          <CardContent className="p-4 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Cargando tipos…
          </CardContent>
        </Card>
      )}

      {error && (
        <Card>
          <CardContent className="p-4 text-sm text-rose-600">
            Error al cargar tipos: {error.message}
          </CardContent>
        </Card>
      )}

      {!loading && !error && tipos.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Boxes className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">
              No hay tipos en {scope === 'interno' ? 'GRUPOLAE' : 'colaboradores'} todavía.
            </p>
            <Button
              variant="secondary"
              className="mt-3"
              onClick={() => setView({ mode: 'crear', tipo: { scope } })}
            >
              <Plus className="w-4 h-4 mr-1" />
              Crear el primero
            </Button>
          </CardContent>
        </Card>
      )}

      {!loading && !error && tipos.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {tipos.map((t) => {
            const Icon = getIcon(t.icon);
            return (
              <Card key={`${t.scope}-${t.slug}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-md bg-slate-100">
                      <Icon className="w-5 h-5 text-[hsl(var(--brand-light))]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-slate-900 truncate">
                          {t.nombre}
                        </h3>
                        {t.has_stock ? (
                          <Badge variant="info">stock</Badge>
                        ) : (
                          <Badge variant="secondary">sin stock</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {(t.categoria && t.categoria.nombre) || 'Sin categoría'} ·{' '}
                        <span className="font-mono">{t.ref_prefix}-{'0'.repeat(t.ref_padding || 4)}</span>
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {typeof t.unidades === 'number'
                          ? `${t.unidades} ${t.unidades === 1 ? 'unidad' : 'unidades'}`
                          : '—'}{' '}
                        · {t.fields_schema?.length || 0} campos
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-slate-100">
                    <button
                      onClick={() => setView({ mode: 'editar', tipo: t })}
                      className="flex items-center gap-1 text-xs text-slate-600 hover:text-[hsl(var(--brand-light))] px-2 py-1 rounded hover:bg-slate-50"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Editar
                    </button>
                    <button
                      onClick={() => setDeleting(t)}
                      className="flex items-center gap-1 text-xs text-slate-600 hover:text-rose-600 px-2 py-1 rounded hover:bg-rose-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Eliminar
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Confirmación de borrado (Dialog del DS v2) */}
      <Dialog
        open={!!deleting}
        onClose={() => !deleteBusy && setDeleting(null)}
        title="Eliminar tipo"
        description={
          deleting
            ? `Se eliminará el tipo "${deleting.nombre}" y su tabla de datos. Esta acción no se puede deshacer.`
            : ''
        }
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setDeleting(null)}
              disabled={deleteBusy}
            >
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmDelete} disabled={deleteBusy}>
              {deleteBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Eliminar definitivamente
            </Button>
          </div>
        }
      >
        {deleting && (deleting.unidades || 0) > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
            Atención: este tipo tiene {deleting.unidades} unidades registradas.
            Al eliminarlo se borrarán también todos sus activos.
          </div>
        )}
      </Dialog>
    </div>
  );
}
